import { useMemo } from 'react';
import { useProperties, useUnits } from './useProperties';
import { useOwners } from './useCore';
import { useBills, useReceivables, useDistributions } from './useAccounting';
import { useTickets } from './useMaintenance';
import {
  calculateOccupancyFromUnits,
  calculateRevenue,
  calculateExpenses,
  calculateNOI,
  calculateROI,
  calculatePendingReceivables,
  calculateOverdueReceivables,
  calculatePendingBills,
  isJobCompleted,
  isJobActive
} from '@/utils/sharedMetrics';
import type { Property, Receivable, Bill, MaintenanceTicket } from '@repo/shared-types';

/**
 * Centralized Owner Metrics Hook
 *
 * Single source of truth for owner-derived metrics. Computation is a pure
 * reactive derivation (useMemo) over already-cached React Query data so the
 * result updates as soon as any source query resolves; we deliberately do NOT
 * wrap this in useQuery because that would snapshot zero-length source arrays
 * at first render and cache the zeros past the point the source data arrives.
 *
 * Owner-facing money summaries should use this hook (or data it exposes) — avoid ad-hoc financial reduces in portal UI.
 */

export interface OwnerMetrics {
  // Property Metrics
  totalProperties: number;
  totalUnits: number;
  occupiedUnits: number;
  occupancyRate: number;

  // Financial Metrics
  totalRevenue: number;
  collectedRevenue: number;
  totalExpenses: number;
  paidExpenses: number;
  noi: number;
  collectedNoi: number;
  roi: number;

  // Cash Flow Metrics
  pendingReceivables: number;
  overdueReceivables: number;
  pendingBills: number;
  distributionsPaid: number;

  // Maintenance Metrics
  activeTickets: number;
  completedTickets: number;
  /** Work orders in Pending Approval on this owner's properties */
  pendingApprovalTicketCount: number;

  // Detailed Data
  properties: Property[];
  receivables: Receivable[];
  bills: Bill[];
  tickets: MaintenanceTicket[];
}

function emptyOwnerMetrics(): OwnerMetrics {
  return {
    totalProperties: 0,
    totalUnits: 0,
    occupiedUnits: 0,
    occupancyRate: 0,
    totalRevenue: 0,
    collectedRevenue: 0,
    totalExpenses: 0,
    paidExpenses: 0,
    noi: 0,
    collectedNoi: 0,
    roi: 0,
    pendingReceivables: 0,
    overdueReceivables: 0,
    pendingBills: 0,
    distributionsPaid: 0,
    activeTickets: 0,
    completedTickets: 0,
    pendingApprovalTicketCount: 0,
    properties: [],
    receivables: [],
    bills: [],
    tickets: [],
  };
}

export const useOwnerMetrics = (ownerId: string | undefined) => {
  const propertiesQ = useProperties();
  const unitsQ = useUnits();
  const ownersQ = useOwners();
  const billsQ = useBills();
  const receivablesQ = useReceivables();
  const ticketsQ = useTickets();
  const distributionsQ = useDistributions();

  const allProperties = propertiesQ.data ?? [];
  const allUnits = unitsQ.data ?? [];
  const owners = ownersQ.data ?? [];
  const allBills = billsQ.data ?? [];
  const allReceivables = receivablesQ.data ?? [];
  const allTickets = ticketsQ.data ?? [];
  const distributions = distributionsQ.data ?? [];

  const isLoading =
    !ownerId ||
    propertiesQ.isLoading ||
    unitsQ.isLoading ||
    ownersQ.isLoading ||
    billsQ.isLoading ||
    receivablesQ.isLoading ||
    ticketsQ.isLoading ||
    distributionsQ.isLoading;

  const isError =
    propertiesQ.isError ||
    unitsQ.isError ||
    ownersQ.isError ||
    billsQ.isError ||
    receivablesQ.isError ||
    ticketsQ.isError ||
    distributionsQ.isError;

  const data = useMemo<OwnerMetrics | undefined>(() => {
    if (!ownerId) return undefined;

    const owner = owners.find((o) => o.id === ownerId);
    if (!owner) return emptyOwnerMetrics();

    const ownerProperties = allProperties.filter((p) =>
      (owner.propertyIds && owner.propertyIds.includes(p.id)) ||
      (p.ownerIds?.includes(ownerId))
    );
    const propertyIds = ownerProperties.map((p) => p.id);

    const ownerReceivables = allReceivables.filter((r) => propertyIds.includes(r.propertyId));
    const ownerBills = allBills.filter((b) => propertyIds.includes(b.propertyId));
    const ownerTickets = allTickets.filter((t) => propertyIds.includes(t.propertyId));
    const ownerUnits = allUnits.filter((u) => propertyIds.includes(u.propertyId));

    const totalProperties = ownerProperties.length;
    const totalUnits = ownerUnits.length;
    const occupancyRate = calculateOccupancyFromUnits(ownerUnits);
    const occupiedUnits = ownerUnits.filter((u) => u.status === 'Occupied').length;

    const totalRevenue = calculateRevenue(ownerReceivables, 'all');
    const collectedRevenue = calculateRevenue(ownerReceivables, 'collected');
    const totalExpenses = calculateExpenses(ownerBills, 'all');
    const paidExpenses = calculateExpenses(ownerBills, 'paid');
    const noi = calculateNOI(ownerReceivables, ownerBills, 'all');
    const collectedNoi = calculateNOI(ownerReceivables, ownerBills, 'collected');

    const portfolioValue = ownerProperties.reduce(
      (sum: number, p: Property) => sum + ((p.noi || 0) * 180),
      0,
    );
    const roi = calculateROI(noi * 12, portfolioValue);

    const pendingReceivables = calculatePendingReceivables(ownerReceivables);
    const overdueReceivables = calculateOverdueReceivables(ownerReceivables);
    const pendingBills = calculatePendingBills(ownerBills);

    const ownerDistributions = distributions.filter((d) => d.ownerId === ownerId);
    const distributionsPaid = ownerDistributions
      .filter((d) => d.status === 'Paid' || d.status === 'Completed')
      .reduce((sum: number, d) => sum + (d.amount || 0), 0);

    const activeTickets = ownerTickets.filter((t) => isJobActive(t)).length;
    const completedTickets = ownerTickets.filter((t) => isJobCompleted(t)).length;
    const pendingApprovalTicketCount = ownerTickets.filter((t) => t.status === 'Pending Approval').length;

    return {
      totalProperties,
      totalUnits,
      occupiedUnits,
      occupancyRate,
      totalRevenue,
      collectedRevenue,
      totalExpenses,
      paidExpenses,
      noi,
      collectedNoi,
      roi,
      pendingReceivables,
      overdueReceivables,
      pendingBills,
      distributionsPaid,
      activeTickets,
      completedTickets,
      pendingApprovalTicketCount,
      properties: ownerProperties,
      receivables: ownerReceivables,
      bills: ownerBills,
      tickets: ownerTickets,
    };
  }, [
    ownerId,
    owners,
    allProperties,
    allUnits,
    allBills,
    allReceivables,
    allTickets,
    distributions,
  ]);

  return { data, isLoading, isError };
};
